// In BranchWiseInvestmentExcelExport.js
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const BranchWiseInvestmentExcelExport = async (params) => {
    let data, startDate, endDate, selectedBranches = [];

    if (params && params.data && params.meta) {
        data = params.data;
        startDate = params.meta.startDate;
        endDate = params.meta.endDate;
        selectedBranches = params.selectedBranches || [];
    } else if (Array.isArray(params)) {
        data = params[0];
        startDate = params[1];
        endDate = params[2];
        selectedBranches = params[3] || [];
    } else {
        console.error("Invalid parameters format");
        return;
    }

    if (!Array.isArray(data) || data.length === 0) {
        console.error("No data provided");
        return;
    }

    const branches = selectedBranches.length
        ? selectedBranches
        : [...new Set(data.map(d => d.branchName))].sort();

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Branch Wise Investment Report");

    /* =========================
       COLUMN DEFINITIONS
    ========================== */
    const columns = [
        { header: "No", key: "no", width: 6 },
        { header: "Invested Bank / Institution", key: "bankName", width: 40 },
        { header: "Treasury Bills (TB) (Rs.)", key: "tbill", width: 22 },
        { header: "Fixed Deposits (FD) (Rs.)", key: "fixed", width: 26 },
        { header: "Treasury Bonds (Rs.)", key: "tbonds", width: 22 },
        { header: "Repos (Rs.)", key: "repos", width: 18 },
        { header: "Other (Rs.)", key: "other", width: 18 },
        { header: "Total (Rs.)", key: "total", width: 22 },
        { header: "%", key: "percentage", width: 12 }
    ];

    // APPLY column widths
    sheet.columns = columns;

    /* =========================
       COLUMN ALIGNMENT & PADDING
    ========================== */
    sheet.getColumn(1).alignment = { horizontal: "center", vertical: "middle" };

    // Bank name column (wrap + padding)
    sheet.getColumn(2).alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
        indent: 2
    };

    // Amount columns (right align + padding)
    [3, 4, 5, 6, 7, 8].forEach(col => {
        sheet.getColumn(col).alignment = {
            horizontal: "right",
            vertical: "middle",
            indent: 1
        };
    });

    sheet.getColumn(9).alignment = {
        horizontal: "center",
        vertical: "middle"
    };

    const amountFormat = "#,##0.00";
    let currentRow = 1;

    /* =========================
       MAIN TITLE
    ========================== */
    sheet.mergeCells(currentRow, 1, currentRow, columns.length);
    sheet.getCell(currentRow, 1).value = "All Island - Branch Wise Investment Report";
    sheet.getCell(currentRow, 1).font = { bold: true, size: 16 };
    sheet.getCell(currentRow, 1).alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(currentRow).height = 32;
    currentRow++;

    /* =========================
       SUBTITLE
    ========================== */
    sheet.mergeCells(currentRow, 1, currentRow, columns.length);
    sheet.getCell(currentRow, 1).value = "Investment Summary As At";
    sheet.getCell(currentRow, 1).font = { bold: true, size: 14 };
    sheet.getCell(currentRow, 1).alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(currentRow).height = 26;
    currentRow++;

    /* =========================
       PERIOD
    ========================== */
    sheet.mergeCells(currentRow, 1, currentRow, columns.length);
    sheet.getCell(currentRow, 1).value = `Investment Period: ${startDate} to ${endDate}`;
    sheet.getCell(currentRow, 1).font = { bold: true };
    sheet.getCell(currentRow, 1).alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(currentRow).height = 22;
    currentRow += 2;

    /* =========================
       PROCESS EACH BRANCH
    ========================== */
    branches.forEach(branch => {
        sheet.mergeCells(currentRow, 1, currentRow, columns.length);
        sheet.getCell(currentRow, 1).value = `Branch: ${branch}`;
        sheet.getCell(currentRow, 1).font = { bold: true, size: 14, color: { argb: "0000FF" } };
        sheet.getCell(currentRow, 1).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "E6F3FF" }
        };
        sheet.getCell(currentRow, 1).alignment = { horizontal: "center", vertical: "middle" };
        sheet.getRow(currentRow).height = 28;
        currentRow++;

        // Table headers
        columns.forEach((col, idx) => {
            const cell = sheet.getCell(currentRow, idx + 1);
            cell.value = col.header;
            cell.font = { bold: true };
            cell.alignment = {
                horizontal: "center",
                vertical: "middle",
                wrapText: true,
                indent: 1
            };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "D9E1F2" }
            };
        });
        sheet.getRow(currentRow).height = 28;
        currentRow++;

        const branchData = data.filter(d => d.branchName === branch);
        const bankGroups = {};

        branchData.forEach(d => {
            const bank = d.bankName || "Unknown Bank";
            bankGroups[bank] ??= { tbill: 0, fixed: 0, tbonds: 0, repos: 0, other: 0 };
            bankGroups[bank].tbill += +d.tbill || 0;
            bankGroups[bank].fixed += +d.fixed || 0;
            bankGroups[bank].tbonds += +d.tbonds || 0;
            bankGroups[bank].repos += +d.repos || 0;
            bankGroups[bank].other += +d.other || 0;
        });

        Object.keys(bankGroups).sort().forEach((bank, i) => {
            const b = bankGroups[bank];
            const total = b.tbill + b.fixed + b.tbonds + b.repos + b.other;

            const row = sheet.getRow(currentRow);

            row.values = [
                i + 1,
                ` ${bank} `,
                b.tbill,
                b.fixed,
                b.tbonds,
                b.repos,
                b.other,
                total,
                0
            ];

            // Auto height for wrapped bank names
            if (bank.length > 35) row.height = 34;     // 2 lines
            if (bank.length > 70) row.height = 46;     // 3 lines
            if (bank.length <= 35) row.height = 24;    // normal

            [3,4,5,6,7,8].forEach(c => {
                sheet.getCell(currentRow, c).numFmt = amountFormat;
            });

            currentRow++;
        });

        currentRow++;
    });

    /* =========================
       BORDERS
    ========================== */
    sheet.eachRow(row => {
        row.eachCell(cell => {
            cell.border = {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" }
            };
        });
    });

    // Freeze header
    sheet.views = [{ state: "frozen", ySplit: 4 }];

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
        new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        }),
        `Branch_Wise_Investment_Report_${startDate.replace(/\//g, "-")}_to_${endDate.replace(/\//g, "-")}.xlsx`
    );
};

export default BranchWiseInvestmentExcelExport;
